import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Icon, Button } from '../../../pages/DesignSystem';
import './ConfirmationModal.css';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  /** Optional list of items to show (e.g., occurrences to be deleted) */
  items?: Array<{ label: string; sublabel?: string }>;
  /** Confirm button text - default: "Confirm" */
  confirmText?: string;
  /** Cancel button text - default: "Cancel" */
  cancelText?: string;
  /** Variant affects the icon and colors */
  variant?: 'warning' | 'danger' | 'info';
  /** Loading state for confirm button */
  isLoading?: boolean;
}

/**
 * ConfirmationModal - Reusable modal for confirming destructive or important actions
 * Replaces window.confirm() with a proper app modal
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  items,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning',
  isLoading = false
}) => {
  // Handle ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, isLoading]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return 'AlertTriangle';
      case 'info':
        return 'Info';
      default:
        return 'AlertCircle';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="confirmation-modal__overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={handleOverlayClick}
        >
          <motion.div
            className={`confirmation-modal__dialog confirmation-modal__dialog--${variant}`}
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="confirmation-modal__header">
              <Icon
                name={getIcon()}
                size={24}
                className={`confirmation-modal__icon confirmation-modal__icon--${variant}`}
              />
              <h2 className="confirmation-modal__title">{title}</h2>
            </div>

            <p className="confirmation-modal__message">{message}</p>

            {items && items.length > 0 && (
              <ul className="confirmation-modal__list">
                {items.slice(0, 5).map((item, index) => (
                  <li key={index} className="confirmation-modal__item">
                    <Icon name="Calendar" size={14} className="confirmation-modal__item-icon" />
                    <div className="confirmation-modal__item-content">
                      <span className="confirmation-modal__item-label">{item.label}</span>
                      {item.sublabel && (
                        <span className="confirmation-modal__item-sublabel">{item.sublabel}</span>
                      )}
                    </div>
                  </li>
                ))}
                {items.length > 5 && (
                  <li className="confirmation-modal__item confirmation-modal__item--more">
                    <Icon name="MoreHorizontal" size={14} />
                    <span>...and {items.length - 5} more</span>
                  </li>
                )}
              </ul>
            )}

            <div className="confirmation-modal__actions">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isLoading}
              >
                {cancelText}
              </Button>
              <Button
                variant={variant === 'danger' ? 'danger' : 'primary'}
                onClick={onConfirm}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : confirmText}
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal;
